import {
  GateMovementDirection,
  GateMovementMessageType as MessageType,
  MetricUnit,
} from '@/__generated__/graphql';
import { Formatters } from '@/hooks/useFormatters';
import { type AuTone } from '@/theme/au-compat';
import { describeGateCloseCode } from '@features/gateCommon/utils/gateCloseCode';
import { formatPosition } from '@features/gateCommon/utils/formatPosition';
import { readPayloadPosition } from '@features/gateCommon/utils/payloadPosition';
import { I18n } from '@lingui/core';
import { t, Trans } from '@lingui/macro';
import {
  ArrowDownward,
  ArrowUpward,
  BackHand,
  Battery4Bar,
  CheckCircleOutline,
  Engineering,
  ErrorOutline,
  HelpOutline,
  PanTool,
  Schedule,
  SvgIconComponent,
  SwapVert,
  VerifiedUser,
  Wifi,
} from '@mui/icons-material';
import { FC, Fragment, PropsWithChildren, ReactNode } from 'react';

/** Código tal como lo guardó la fila, o `null` si no trae uno legible. */
export const readCode = (code: unknown): string | null => {
  if (typeof code !== 'string') return null;
  const normalized = code.trim().toUpperCase();
  return normalized.length > 0 ? normalized : null;
};

export interface NotificationData {
  id: number;
  type: MessageType;
  timestamp: number;
  payload?: Record<string, string | number | null> | null;
  /**
   * Código real del evento del firmware (AMARU-862/US-005). `type` es su mapeo, y nueve códigos de
   * cierre distintos comparten el tipo `FINALIZED_WITH_ERROR`.
   */
  rawCode?: string | null;
}

/**
 * Los cuatro tonos del feed. Es **uno más** que los de un estado de movimiento (`MovementTone`):
 * un mensaje puede avisar sin que la operación haya salido mal —límite alcanzado, modo manual,
 * detención remota— y eso es `warning`, que no es un estado en el que un movimiento pueda estar.
 */
export type NotificationTone = Extract<AuTone, 'info' | 'success' | 'warning' | 'error'>;

/** Código del mensaje: el del payload y, de respaldo, el de la fila. */
export const readMessageCode = ({ payload, rawCode }: NotificationData) =>
  readCode(payload?.closeCode) ?? readCode(rawCode);

type Attributes = {
  Icon: SvgIconComponent;
  /**
   * El tono, no un color: la tarjeta lo resuelve contra `AU_TINT` con `auToneSurfaceSx`, que es la
   * misma receta que llevan la banda de estado y el chip de la tabla de movimientos.
   */
  tone: NotificationTone;
};

type TextFormatters = {
  /** La línea de la tarjeta: «qué pasó · dato», o «título · acción» en un cierre con código. */
  titleFormatter: (
    payload: NotificationData['payload'],
    Accent?: FC<PropsWithChildren>,
  ) => ReactNode;
};

/** Un segmento vacío se omite: la línea nunca termina en « · ». */
export const renderLine = (
  text: string,
  { value, detail }: { value?: string | null; detail?: string | null },
  Accent?: FC<PropsWithChildren>,
): ReactNode => (
  <>
    {text}
    {value && (
      <>
        {' · '}
        {Accent ? <Accent>{value}</Accent> : value}
      </>
    )}
    {detail && ` · ${detail}`}
  </>
);

/**
 * El mensaje sin color va en `info`, el acromático de la capa, y no en el `#222B36` que llevaba:
 * aquel era una superficie —el fondo de una card—, no un tono, así que el mensaje neutro no
 * pertenecía al mismo eje que sus tres hermanos y los cuatro no se leían como una familia.
 */
/**
 * Un `MOVEMENT` no conoce su sentido: lo hereda del movimiento (`direction`, que resuelve el
 * backend). Abrir = subir; sin sentido conocido, ícono neutro. Los límites tienen flecha fija.
 */
const MOVEMENT_ICON_BY_DIRECTION: Record<GateMovementDirection, SvgIconComponent> = {
  [GateMovementDirection.Open]: ArrowUpward,
  [GateMovementDirection.Close]: ArrowDownward,
};

export const getAttributes = (
  type: MessageType,
  direction?: GateMovementDirection | null,
): Attributes => {
  const DEFAULT_ATTRIBUTES: Attributes = { Icon: HelpOutline, tone: 'info' };

  const iconMap: Record<MessageType, Attributes> = {
    [MessageType.Initializing]: { Icon: Wifi, tone: 'info' },
    [MessageType.StopOperation]: { Icon: PanTool, tone: 'warning' },
    [MessageType.Complete]: { Icon: CheckCircleOutline, tone: 'success' },
    [MessageType.Battery]: { Icon: Battery4Bar, tone: 'info' },
    [MessageType.Movement]: {
      Icon: direction ? MOVEMENT_ICON_BY_DIRECTION[direction] : SwapVert,
      tone: 'info',
    },
    [MessageType.ValidateMovement]: { Icon: VerifiedUser, tone: 'info' },
    [MessageType.EmergencyStop]: { Icon: BackHand, tone: 'error' },
    [MessageType.FinalizedWithError]: { Icon: ErrorOutline, tone: 'error' },
    [MessageType.LowerLimit]: { Icon: ArrowDownward, tone: 'warning' },
    [MessageType.UpperLimit]: { Icon: ArrowUpward, tone: 'warning' },
    [MessageType.ManualMode]: { Icon: Engineering, tone: 'warning' },
    [MessageType.Queued]: { Icon: Schedule, tone: 'info' },
  };

  return iconMap[type] ?? DEFAULT_ATTRIBUTES;
};

export const getTextFormatters = (
  type: MessageType,
  i18n: I18n,
  formatters: Formatters,
): TextFormatters => {
  const DEFAULT_TEXT_FORMATTERS: TextFormatters = {
    titleFormatter: () => t(i18n)`Mensaje de la compuerta`,
  };

  /**
   * Causa real del cierre, cuando el mensaje la trae. Los tipos terminales colapsan códigos que el
   * operario necesita distinguir —un atasco, un timeout y una falla del actuador se arreglan de
   * maneras distintas—, así que el título y la acción del código ganan sobre el texto del tipo.
   */
  const closeOf = (payload: NotificationData['payload']) =>
    describeGateCloseCode(payload?.closeCode, i18n);

  /**
   * Posición reportada por el mensaje, ya formateada, o `null` sin dato. La lectura del payload es
   * una sola (`readPayloadPosition`, espejo del backend): acepta `pos` del firmware nuevo y
   * `position` de las filas anteriores. `0` es la compuerta cerrada, no ausencia de dato.
   */
  const positionOf = (payload: NotificationData['payload']) => {
    const position = readPayloadPosition(payload);
    if (position == null) return null;
    return formatPosition(position, formatters.formatMetricValue);
  };

  const batteryOf = (payload: NotificationData['payload']) => {
    const level = payload?.batteryLevel;
    if (typeof level !== 'number') return null;
    return formatters.formatMetricValue(level, { unit: MetricUnit.Percent, precision: 1 });
  };

  /**
   * Vencimiento de una solicitud en cola, ya formateado, o `null` sin fecha legible. El payload de
   * la siembra trae `expiresAt` como ISO-8601.
   */
  const expiresOnOf = (payload: NotificationData['payload']) => {
    const raw = payload?.expiresAt;
    const expiresAt = typeof raw === 'string' ? Date.parse(raw) : NaN;
    if (!Number.isFinite(expiresAt)) return null;
    return formatters.formatDate(expiresAt, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /** Un tipo de cierre: con código se leen su título y su acción; sin código, el texto del tipo. */
  const closing =
    (
      fallback: string,
      fallbackDetail?: string,
      withPosition = false,
    ): TextFormatters['titleFormatter'] =>
    (payload, Accent) => {
      const close = closeOf(payload);
      const value = withPosition ? positionOf(payload) : null;
      if (close) return renderLine(close.title, { value, detail: close.action }, Accent);
      return renderLine(fallback, { value, detail: fallbackDetail }, Accent);
    };

  const formattersMap: Record<MessageType, TextFormatters> = {
    [MessageType.Initializing]: {
      titleFormatter: () => t(i18n)`Enviando la solicitud al equipo`,
    },
    [MessageType.ValidateMovement]: {
      titleFormatter: () => t(i18n)`Movimiento validado por el equipo`,
    },
    [MessageType.Movement]: {
      titleFormatter: (payload, Accent) =>
        renderLine(t(i18n)`En movimiento`, { value: positionOf(payload) }, Accent),
    },
    [MessageType.Battery]: {
      titleFormatter: (payload, Accent) =>
        renderLine(t(i18n)`Batería`, { value: batteryOf(payload) }, Accent),
    },
    // COMPLETE conserva la posición alcanzada aunque traiga código: es el dato operativo.
    [MessageType.Complete]: {
      titleFormatter: closing(t(i18n)`Operación completada`, undefined, true),
    },
    [MessageType.StopOperation]: {
      titleFormatter: closing(t(i18n)`Operación detenida`),
    },
    [MessageType.EmergencyStop]: {
      titleFormatter: closing(
        t(i18n)`Parada de emergencia activa`,
        t(i18n)`desactivarla en terreno`,
      ),
    },
    [MessageType.FinalizedWithError]: {
      titleFormatter: closing(t(i18n)`Operación finalizada con error`),
    },
    [MessageType.LowerLimit]: {
      titleFormatter: () => t(i18n)`Compuerta alcanzó el límite inferior`,
    },
    [MessageType.UpperLimit]: {
      titleFormatter: () => t(i18n)`Compuerta alcanzó el límite superior`,
    },
    [MessageType.ManualMode]: {
      titleFormatter: () => t(i18n)`Compuerta en modo manual`,
    },
    // `tgt` es el destino pedido, no una posición reportada: no se muestra.
    [MessageType.Queued]: {
      titleFormatter: (payload, Accent) => {
        const expiresOn = expiresOnOf(payload);
        if (expiresOn == null) {
          return renderLine(t(i18n)`En cola`, {
            detail: t(i18n)`se enviará cuando el equipo se conecte`,
          });
        }
        const Value = Accent ?? Fragment;
        return (
          <Trans>
            En cola · vence el <Value>{expiresOn}</Value>
          </Trans>
        );
      },
    },
  };
  return formattersMap[type] ?? DEFAULT_TEXT_FORMATTERS;
};
